import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  User,
  onAuthStateChanged,
  signOut,
  signInWithPopup,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  sendEmailVerification,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../../firebase/config";
import { endSmsEnrollmentSession, smsEnrollmentSessionActive } from "../../utils/firebaseSms";

const SUPER_ADMIN_EMAIL = "robertwangca@gmail.com";

interface AuthContextType {
  currentUser: User | null;
  isAuthStateChanged: boolean;
  isSuperAdmin: boolean;
  needsAuthenticator: boolean;
  logout: () => Promise<void>;
  signInWithGoogle: () => Promise<any>;
  forgotPassword: (email: string) => Promise<void>;
  sendVerification: () => Promise<void> | undefined;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  isAuthStateChanged: false,
  isSuperAdmin: false,
  needsAuthenticator: false,
  logout: async () => {},
  signInWithGoogle: async () => {},
  forgotPassword: async () => {},
  sendVerification: () => undefined,
});

export const useAuth = () => useContext(AuthContext);

type Props = {
  children: ReactNode;
};

const AuthContextProvider = ({ children }: Props) => {
  const [isAuthStateChanged, setIsAuthStateChanged] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [needsAuthenticator, setNeedsAuthenticator] = useState(false);

  function signInWithGoogle() {
    const provider = new GoogleAuthProvider();
    return signInWithPopup(auth, provider);
  }

  function sendVerification() {
    if (auth.currentUser) {
      return sendEmailVerification(auth.currentUser);
    }
  }

  function forgotPassword(email: string) {
    return sendPasswordResetEmail(auth, email, {
      url: window.location.protocol + "//" + window.location.host + "/login",
    });
  }

  function logout() {
    endSmsEnrollmentSession();
    return signOut(auth);
  }

  const value: AuthContextType = {
    isAuthStateChanged,
    currentUser,
    isSuperAdmin,
    needsAuthenticator,
    logout,
    signInWithGoogle,
    forgotPassword,
    sendVerification,
  };

  useEffect(() => {
    let generation = 0;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const current = ++generation;
      if (!user) setIsAuthStateChanged(false);

      const finish = async () => {
        if (!user) {
          setCurrentUser(null);
          setIsSuperAdmin(false);
          setNeedsAuthenticator(false);
          return;
        }

        const emailMatches = user.email?.toLowerCase() === SUPER_ADMIN_EMAIL;
        if (!emailMatches) {
          setCurrentUser(user);
          setIsSuperAdmin(false);
          setNeedsAuthenticator(false);
          return;
        }

        try {
          const token = await user.getIdTokenResult();
          if (current !== generation) return;
          if (token.claims.superAdminSession === true || token.signInSecondFactor === "phone") {
            setCurrentUser(user);
            setIsSuperAdmin(true);
            setNeedsAuthenticator(false);
            return;
          }
          const status = await httpsCallable<void, { isTwoFactorEnabled?: boolean }>(
            functions,
            "adminAccountStatus"
          )();
          if (current !== generation) return;
          if (status.data.isTwoFactorEnabled === true && !smsEnrollmentSessionActive()) {
            setCurrentUser(user);
            setIsSuperAdmin(false);
            setNeedsAuthenticator(true);
            return;
          }
          setNeedsAuthenticator(false);
          setCurrentUser(user);
          setIsSuperAdmin(true);
        } catch (error) {
          if (current !== generation) return;
          setNeedsAuthenticator(false);
          setCurrentUser(user);
          setIsSuperAdmin(true);
        }
      };

      finish().finally(() => {
        if (current === generation) setIsAuthStateChanged(true);
      });
    });

    return () => {
      generation += 1;
      unsubscribe();
    };
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
